import React, { useMemo } from 'react';
import { DiaryEvent } from '../../graphql/entities';
import attributeQuery from '../../graphql/queries/diary/DiaryAttributeConfigListQuery.graphql';
import Formatters from '../../components/grid-addons/formatters';
import { useWorkspace } from '../../graphql/hooks/useWorkspace';
import { useAttributesForWorkspace } from '../../components/Workspace/useAttributesForWorkspace';
import { IEvent } from '../../graphql/schema/p4types';
import Workspace from '../../components/Workspace/Workspace';
import customerAttributeQuery from 'graphql/queries/customer/CustomerAttributeConfigListQuery.graphql';
import supplierAttributeQuery from 'graphql/queries/supplier/SupplierAttributeConfigListQuery.graphql';
import { ModalFormatter } from '../../components/grid-addons/formatters/ModalFormatter';
import { IEditableDataGridColumnDef } from '../../@types/editableDataGridTypes';

const LazyCustomerForm = React.lazy(() => import('../../views/customer/CustomerForm'));
const LazySupplierForm = React.lazy(() => import('../../views/supplier/SupplierForm'));

const { formUrl, type, title, workspace } = DiaryEvent;

const DIARY_EVENT_FIELD_NAMES = ['subject', 'closed', 'eventStart', 'eventEnd'];

const availableFiltersFilter = (column: IEditableDataGridColumnDef) => {
  return column?.fieldName && !column.fieldName.includes('duration');
};

const DiaryEventWorkspace = (props) => {
  const columnOverrides = useMemo(
    () => ({
      eventStart: {
        name: 'Start',
        resizable: true,
        formatter: 'dateTime',
      },
      eventEnd: {
        name: 'End',
        resizable: true,
        formatter: 'dateTime',
      },
      subject: {
        name: 'Subject',
        resizable: true,
      },
      duration: {
        name: 'Duration',
        resizable: true,
        formatter: 'duration',
      },
      closed: {
        name: 'Closed',
        resizable: true,
        formatter: Formatters.BooleanFormatter,
      },
      'customer.reference': {
        name: 'Customer',
        formatter: ModalFormatter,
        width: 125,
        options: {
          modalContent: LazyCustomerForm,
          modalProps: { forceReadOnly: true },
          entityId: 'customer.id',
        },
      },
      'supplier.reference': {
        name: 'Supplier',
        formatter: ModalFormatter,
        width: 125,
        options: {
          modalContent: LazySupplierForm,
          modalProps: { forceReadOnly: true },
          entityId: 'supplier.id',
        },
      },
    }),
    []
  );

  const attributeColumns = useAttributesForWorkspace(attributeQuery);

  const customerAttributeColumns = useAttributesForWorkspace(customerAttributeQuery, {
    valuesPath: 'customer.attributeValues',
    columnKey: 'customer.attributeValues.attributeConfig.title',
  });

  const supplierAttributeColumns = useAttributesForWorkspace(supplierAttributeQuery, {
    valuesPath: 'supplier.attributeValues',
    columnKey: 'supplier.attributeValues.attributeConfig.title',
  });

  const customColumns = useMemo(() => {
    return {
      ...(props.customColumns || {}),
      ...attributeColumns,
      ...customerAttributeColumns,
      ...supplierAttributeColumns,
    };
  }, [props.customColumns, attributeColumns, customerAttributeColumns, supplierAttributeColumns]);

  const workspaceProps = useWorkspace<IEvent>(props, DiaryEvent, {
    columns: DIARY_EVENT_FIELD_NAMES,
    customColumns: customColumns,
    columnOverrides: columnOverrides,
    initialSortColumn: 'eventStart',
  });

  return (
    <Workspace
      entityType={type}
      title={title}
      formUrl={formUrl}
      defaultQuery={workspace}
      addButtons={[
        {
          url: `${formUrl}New`,
          description: 'Add Task',
        },
        {
          url: '/Diary/RecurringEvent/Details/New',
          description: 'Add Recurring Tasks',
        },
      ]}
      availableFiltersFilter={availableFiltersFilter}
      viewScreenName="Diary/Task/Workspace"
      {...workspaceProps}
      {...props}
      entity={DiaryEvent}
    />
  );
};

export default DiaryEventWorkspace;
